import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';

// Components
import FilterItem from '../../molecules/FilterItem/FilterItem';
import Button from '../../atoms/Button/Button';
import DndListItem from '../../atoms/DndListItem/DndListItem';
import IconButton from '../../atoms/IconButton/IconButton';
import SelectField from '../../atoms/Select/Select';

// Styles
import styles from './SortByForm.module.css';

/**
 * SortByForm – simple container form for sort by
 * @param {object} props
 * @param {boolean} props.isLoading
 * @param {boolean} props.isSubmitting
 * @param {(data: ApiManagerTableFiltersSortByCriteriaValue[]) => void} props.onSubmit
 * @param {ReturnType<typeof import('../../../domain/tableSortBy/useTableSortBy').default>} props.tableSortBy
 * @param {ApiManagerTableFiltersSortBy} props.sortBy
 * @returns {JSX.Element}
 */
function SortByForm({
  isLoading,
  isSubmitting,
  onSubmit,
  tableSortBy,
  sortBy,
}) {
  const { t } = useTranslation();

  const {
    filtersById,
    formFilters: { control: filtersControl, handleSubmit: handleSubmitFilters },
    filtersFields,
    handleAddFilter,
    handleUpdateTypeFilter,
    handleUpdateOrderByFilter,
    handleRemoveFilter,
    handleFiltersOrder,
    handleCancelFilters,
    handleClearAllSortByFilters,
    canAddFilter,
  } = tableSortBy;

  return (
    <form
      className={`${styles.container}`}
      onSubmit={handleSubmitFilters((data) => {
        /** @type {{ filters: ApiManagerTableFiltersSortByCriteriaValue[] }} */
        const typedData = data;
        onSubmit(typedData.filters.filter((filter) => filter.id));
      })}
    >
      <SortByHeader title={t('table.filters.sort')}>
        <Button
          color="no-background"
          icon="refresh"
          size="small"
          iconPosition="right"
          onClick={() => handleClearAllSortByFilters()}
        >
          {t('table.filters.clearAll')}
        </Button>
      </SortByHeader>
      <SortByContent
        actions={(
          <Button
            color="no-background"
            icon="add"
            size="small"
            iconPosition="right"
            onClick={() => handleAddFilter()}
            disabled={!canAddFilter}
          >
            {t('table.filters.addFilter')}
          </Button>
        )}
      >
        <DndListItem
          name="filters"
          items={filtersFields}
          handleSaveDndOrder={handleFiltersOrder}
          idKey="_id"
        >
          {(items) => items.map((/** @type {ApiManagerTableFiltersSortByCriteriaValue & {_id: string}} */ item, index) => (
            <FilterItem
              // eslint-disable-next-line no-underscore-dangle
              key={item?._id}
              // eslint-disable-next-line no-underscore-dangle
              id={item?._id}
              action={index > 0 ? (
                <IconButton
                  icon="trash-empty"
                  color="transparent"
                  size="medium"
                  onClick={() => handleRemoveFilter(index)}
                />
              ) : (
                <span className={styles['sort-action-spacer']} aria-hidden />
              )}
            >
              <>
                <Controller
                  name={`filters.${index}.id`}
                  control={filtersControl}
                  render={({
                    field: { ref, ...fieldInput },
                    fieldState: { error },
                  }) => (
                    <SelectField
                      id={`filters.${index}.id`}
                      value={fieldInput.value}
                      onChange={(
                        /** @type {{ label: string, value: string }} */ value,
                      ) => {
                        handleUpdateTypeFilter(index, {
                          id: value?.value,
                          order: '',
                        });
                        fieldInput.onChange(value.value);
                      }}
                      options={sortBy?.criteria?.map(
                        (criteria) => ({
                          value: criteria.id,
                          label: criteria.lib,
                        }),
                      ) ?? []}
                      placeholder={t('table.filters.selectType')}
                      error={t(error?.message || '')}
                    />
                  )}
                />
                <Controller
                  name={`filters.${index}.order`}
                  control={filtersControl}
                  render={({
                    field: { ref, ...fieldInput },
                    fieldState: { error },
                  }) => (
                    <SelectField
                      id={`filters.${index}.order`}
                      value={fieldInput.value}
                      onChange={(
                        /** @type {{ label: string, value: 'asc' | 'desc' }} */ value,
                      ) => {
                        handleUpdateOrderByFilter(index, {
                          id: item.id,
                          order: value?.value,
                        });
                        fieldInput.onChange(value.value);
                      }}
                      options={filtersById[item.id]?.orderOptions?.map(
                        (orderOption) => ({
                          value: orderOption,
                          label: t(`table.filters.orderOptions.${orderOption}`),
                        }),
                      ) ?? []}
                      placeholder={t('table.filters.selectOrderBy')}
                      error={t(error?.message || '')}
                      disabled={!item.id}
                    />
                  )}
                />
              </>
            </FilterItem>
          ))}
        </DndListItem>
      </SortByContent>
      <SortByFooter
        cancelAction={(
          <Button
            color="primary100"
            size="small"
            onClick={handleCancelFilters}
          >
            {t('table.filters.cancel')}
          </Button>
                  )}
        applyAction={(
          <Button
            size="small"
            isSubmit
            isLoading={isLoading || isSubmitting}
          >
            {t('table.filters.apply')}
          </Button>
        )}
      />
    </form>
  );
}

/**
 * SortByHeader – header with optional title and actions
 * @param {object} props
 * @param {import('react').ReactNode} [props.title]
 * @param {import('react').ReactNode} [props.children]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function SortByHeader({
  title = null, className = '', children,
}) {
  return (
    <div className={`${styles.header} ${className}`}>
      <div className={styles['header-title']}>
        {title}
      </div>
      <div className={styles['header-actions']}>
        {children}
      </div>
    </div>
  );
}

/**
 * SortByHeaderTitle – header title component
 * @param {object} props
 * @param {import('react').ReactNode} props.children
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function SortByHeaderTitle({ children, className = '' }) {
  return (
    <h3 className={`${styles['header-title']} ${className}`}>
      {children}
    </h3>
  );
}

/**
 * SortByContent – scrollable content area
 * @param {object} props
 * @param {import('react').ReactNode} props.children
 * @param {import('react').ReactNode} [props.actions]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function SortByContent({ children, actions = null, className = '' }) {
  return (
    <div className={`${styles.content} ${className}`}>
      <div className={styles['content-body']}>
        {children}
      </div>
      {actions && (
        <div className={styles['content-actions']}>
          {actions}
        </div>
      )}
    </div>
  );
}

/**
 * SortByFooter – footer with actions
 * @param {object} props
 * @param {import('react').ReactNode} [props.cancelAction]
 * @param {import('react').ReactNode} [props.applyAction]
 * @param {string} [props.className]
 * @param {import('react').ReactNode} [props.children]
 * @returns {JSX.Element}
 */
function SortByFooter({
  cancelAction = null, applyAction = null, className = '', children,
}) {
  return (
    <div className={`${styles.footer} ${className}`}>
      {children && (
        <div className={styles['footer-content']}>
          {children}
        </div>
      )}
      <div className={styles['footer-actions']}>
        {cancelAction}
        {applyAction}
      </div>
    </div>
  );
}

export {
  SortByForm,
  SortByHeader,
  SortByHeaderTitle,
  SortByContent,
  SortByFooter,
};

export default SortByForm;
